import React, { useEffect, useState } from 'react';
import api from '../api';

// Componente para flujo incrustado de Embedded Signup (Meta WhatsApp)
// Ahora incluye lógica para ocultar el botón cuando ya está conectado.

const APP_ID = '2874656366065985';
const GRAPH_VERSION = import.meta.env.VITE_META_GRAPH_VERSION || 'v24.0';
const CONFIG_ID = import.meta.env.VITE_META_EMBEDDED_CONFIG_ID || '1589022149133006';
console.info('[EmbeddedSignup] Build env:', { CONFIG_ID, GRAPH_VERSION });

const EmbeddedSignupButton = ({ onSuccess }) => {
  const [sdkReady, setSdkReady] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [config, setConfig] = useState(null);
  const [connected, setConnected] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  // Número capturado directamente del flujo incrustado (prioritario)
  const [flowPhoneId, setFlowPhoneId] = useState("");
  const [flowDisplayPhone, setFlowDisplayPhone] = useState("");
  const [clientPhoneNumber, setClientPhoneNumber] = useState('');
  const USE_ACCESS_TOKEN_FLOW = false; // sólo soportamos flujo code

  // Asegurar casillero vacío al cargar y limpiar flags de reconexión si existen
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem('client_phone_number');
        const saved = window.localStorage.getItem('client_phone_number');
        if (!saved) setClientPhoneNumber('');
      }
    } catch (_) {}
  }, []);

  const refreshConfig = async () => {
    try {
      setLoadingConfig(true);
      const r = await api.get('/integration/meta-config/');
      setConfig(r.data);
      const isActive = !!r.data?.is_active;
      const completed = r.data?.signup_state === 'completed';
      const hasNumber = !!(r.data?.phone_number_id && r.data?.display_phone_number);
      const force = (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('forceMetaReconnect') === '1');
      // Solo considerar conectado si hay número asignado; ignorar 'completed' sin número
      setConnected(!force && isActive && hasNumber);
    } catch (e) {
      console.warn('[EmbeddedSignup] error obteniendo meta-config', e);
      if (e?.response?.status === 401) {
        alert('Tu sesión expiró. Inicia sesión nuevamente para continuar el flujo.');
      }
    } finally {
      setLoadingConfig(false);
    }
  };

  useEffect(() => { refreshConfig(); }, []);

  useEffect(() => {
    let intervalId;
    const checkFB = () => {
      if (window.FB) {
        try {
          window.FB.init({ appId: APP_ID, autoLogAppEvents: true, xfbml: true, version: GRAPH_VERSION });
        } catch (_) {}
        setSdkReady(true);
        clearInterval(intervalId);
      }
    };
    intervalId = setInterval(checkFB, 300);

    const onMessage = (event) => {
      if (!event.origin.endsWith('facebook.com')) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          console.log('[WA_EMBEDDED_SIGNUP message]', data);
          api.post('/integration/meta-embedded-event/', data).catch(() => {});
          // Captura de número seleccionado dentro del flujo (si viene en el payload)
          try {
            const payload = data?.data || {};
            const candidateId = payload.phone_number_id || payload.phone_number?.id || payload.selected_phone_number_id || '';
            const candidateDisplay = payload.display_phone_number || payload.phone_number?.display_phone_number || payload.selected_display_phone_number || '';
            const candidateWaba = payload.waba_id || payload.whatsapp_business_account_id || '';
            const candidateBusiness = payload.business_id || payload.business_account_id || '';
            if (candidateId) setFlowPhoneId(String(candidateId));
            if (candidateDisplay) setFlowDisplayPhone(String(candidateDisplay));
            // Guardar hints en localStorage para usarlos si el backend devuelve pending
            try {
              // No auto-prellenar el número en el input; solo guardar hints de WABA/Business.
              if (candidateWaba) localStorage.setItem('hint_waba_id', String(candidateWaba));
              if (candidateBusiness) localStorage.setItem('hint_business_id', String(candidateBusiness));
            } catch(_) {}
          } catch (_) {}
          const flowEvent = (data.event || '').toUpperCase();
          if (['FINISHED','COMPLETED','SUCCESS'].includes(flowEvent)) {
            // Manejado por fbLoginCallback luego
          } else if (['CANCEL','ABANDONED'].includes(flowEvent)) {
            const step = data?.data?.current_step || 'desconocido';
            const errMsg = data?.data?.error_message;
            const errId = data?.data?.error_id;
            alert('Flujo cancelado. Paso: ' + step + (errMsg ? '\nMotivo: ' + errMsg : '') + (errId ? '\nID Error: ' + errId : ''));
            setLaunching(false);
          } else if (['ERROR','FAILED'].includes(flowEvent)) {
            const errMsg = data?.data?.error_message || 'Error desconocido';
            const errId = data?.data?.error_id ? (' ID: ' + data.data.error_id) : '';
            alert('Error en flujo incrustado: ' + errMsg + errId + '\nVerifica permisos y configuración.');
            setLaunching(false);
          }
        }
      } catch (err) {
        console.log('[WA_EMBEDDED_SIGNUP raw message]', event.data);
        api.post('/integration/meta-embedded-event/', { raw: event.data, type: 'WA_EMBEDDED_SIGNUP', event: 'UNKNOWN_PARSE_ERROR' }).catch(() => {});
      }
    };
    window.addEventListener('message', onMessage);
    return () => { window.removeEventListener('message', onMessage); clearInterval(intervalId); };
  }, []);

  const completeSignup = async (code) => {
    try {
      const resp = await api.post('/integration/meta-complete-signup/', { code });
      if (resp.data.success) {
        alert('✅ Número conectado correctamente (complete-signup)');
        await refreshConfig();
        onSuccess && onSuccess();
      } else {
        alert('Error al completar signup: ' + (resp.data.error || 'desconocido'));
      }
    } catch (err) {
      alert('Fallo completando signup: ' + (err.response?.data?.error || err.message));
    } finally { setLaunching(false); }
  };

  const fbLoginCallback = (response) => {
    setDebugInfo(prev => ({ ...prev, callbackRaw: response }));
    const waitForAssignedNumber = async (maxAttempts = 30, delayMs = 2000) => {
      for (let i = 0; i < maxAttempts; i++) {
        try {
          const [cfgResp, statusResp] = await Promise.all([
            api.get('/integration/meta-config/'),
            api.get('/integration/connection-status/')
          ]);
          const cfg = cfgResp.data || {};
          const st = statusResp.data || {};
          const hasNumber = !!((cfg.phone_number_id && cfg.display_phone_number) || (st?.details?.phone_number_id && st?.details?.display_phone_number));
          if (hasNumber) {
            try { if (typeof window !== 'undefined' && window.localStorage) { window.localStorage.removeItem('forceMetaReconnect'); } } catch {}
            setConfig(cfg);
            setConnected(true);
            onSuccess && onSuccess();
            return true;
          }
        } catch (e) {
          // Si el token expiró durante el polling, pedir relogin para evitar estado azul permanente
          if (e?.response?.status === 401) {
            alert('Tu sesión expiró durante la verificación. Inicia sesión y reintenta.');
            break;
          }
        }
        await new Promise(res => setTimeout(res, delayMs));
      }
      return false;
    };
    if (USE_ACCESS_TOKEN_FLOW && response.authResponse?.accessToken) {
      api.post('/integration/meta-access-token/', {
        access_token: response.authResponse.accessToken,
        config_id: CONFIG_ID,
        // Hints de número priorizando el capturado del flujo
        hint_phone_number_id: flowPhoneId || undefined,
        hint_display_phone_number: flowDisplayPhone || undefined
      })
        .then(async r => { console.log('Respuesta backend access-token', r.data); alert('✅ Token recibido.'); const ok = await waitForAssignedNumber(); if (!ok) { await refreshConfig(); } })
        .catch(err => { console.warn('Error enviando access token', err); alert('Token recibido pero fallo al enviarlo al backend.'); })
        .finally(() => setLaunching(false));
    } else if (!USE_ACCESS_TOKEN_FLOW && response.authResponse?.code) {
      console.log('[Signup code]', response.authResponse.code);
      api.post('/integration/meta-access-token/', {
        code: response.authResponse.code,
        config_id: CONFIG_ID,
        hint_phone_number_id: flowPhoneId || undefined,
        hint_display_phone_number: flowDisplayPhone || undefined,
        hint_waba_id: (typeof window!=='undefined' && window.localStorage ? localStorage.getItem('hint_waba_id') : undefined) || undefined,
        hint_business_id: (typeof window!=='undefined' && window.localStorage ? localStorage.getItem('hint_business_id') : undefined) || undefined,
        // Enviar número del cliente como ayuda para autodiscovery
        client_phone_number: (clientPhoneNumber || flowDisplayPhone || '').replace(/[^0-9]/g, '') || undefined
      })
        .then(async r => {
          console.log('Respuesta backend code-exchange', r.data);
          if (r.data?.pending) {
            alert('Conexión iniciada, número no detectado aún. Ingresa tu número y ejecuta "Completar activación".');
          } else {
            alert('✅ Conexión iniciada. Verificando asignación de número...');
          }
          const ok = await waitForAssignedNumber();
          if (!ok) { await refreshConfig(); }
        })
        .catch(err => { console.warn('Error enviando code', err); alert('Error en intercambio: ' + (err.response?.data?.error || err.message)); })
        .finally(() => setLaunching(false));
    } else {
      console.log('[Signup error/response]', response);
      alert('Flujo cancelado o sin credenciales');
      setLaunching(false);
    }
  };

  const manualMetaActivation = async () => {
    try {
      const payload = {};
      // Priorizar número del flujo
      if (flowPhoneId) payload.phone_number_id = flowPhoneId;
      if (flowDisplayPhone) payload.hint_display_phone_number = flowDisplayPhone;
      // Hints del flujo
      try {
        const w = (typeof window!=='undefined' && window.localStorage ? localStorage.getItem('hint_waba_id') : null);
        const b = (typeof window!=='undefined' && window.localStorage ? localStorage.getItem('hint_business_id') : null);
        if (w) payload.hint_waba_id = w;
        if (b) payload.hint_business_id = b;
      } catch(_) {}
      // Enviar también el número del cliente si está disponible
      if (clientPhoneNumber || flowDisplayPhone) payload.client_phone_number = (clientPhoneNumber || flowDisplayPhone).replace(/[^0-9]/g, '');
      try { if (typeof window!== 'undefined' && window.localStorage) { window.localStorage.setItem('client_phone_number', payload.client_phone_number || ''); } } catch(_) {}
      await api.post('/integration/meta-complete-signup-manual/', payload);
      await refreshConfig();
      alert('Activación manual ejecutada. Estado actualizado.');
    } catch (e) {
      alert('Error en activación manual: ' + (e?.response?.data?.detail || e?.message));
    }
  };

  const launchWhatsAppSignup = () => {
    if (!window.FB || !sdkReady) return;
    setLaunching(true);
    const stateValue = 'ws_' + Date.now();
    
    // Implementación exacta solicitada para coexistencia
    const loginOptions = {
      config_id: CONFIG_ID,
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        setup: {},
        featureType: 'whatsapp_business_app_onboarding',
        sessionInfoVersion: '3'
      },
      state: stateValue
    };

    console.log('[EmbeddedSignup] Launching FB.login with options:', loginOptions);
    setDebugInfo({ launchedAt: new Date().toISOString(), options: loginOptions });
    window.FB.login(fbLoginCallback, loginOptions);
  };

  const buttonStyle = {
    backgroundColor: '#1877f2', border: 0, borderRadius: '4px', color: '#fff', cursor: (!sdkReady || launching || CONFIG_ID === '<CONFIGURATION_ID>') ? 'not-allowed' : 'pointer', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '16px', fontWeight: 'bold', height: '40px', padding: '0 24px', opacity: (!sdkReady || CONFIG_ID === '<CONFIGURATION_ID>') ? 0.7 : 1
  };

  if (loadingConfig) return <div style={{padding:'8px',fontSize:14}}>Cargando configuración...</div>;

  if (connected) {
    return (
      <div style={{border:'1px solid #28a745',background:'#e9f9ef',padding:'12px',borderRadius:6,display:'flex',flexDirection:'column',gap:6,fontFamily:'Helvetica, Arial, sans-serif'}}>
        <div style={{fontSize:11,color:'#64748b'}}>build: 2025-12-16</div>
        <div style={{fontWeight:'bold',color:'#1d6f34'}}>✅ hatsApp Business conectado</div>
        <div style={{fontSize:13,color:'#2d5239'}}>Número: {config?.display_phone_number || config?.phone_number || '—'}</div>
        <div style={{fontSize:12,color:'#2d5239'}}>WABA ID: {config?.waba_id || '—'}</div>
        
        <button type="button" onClick={async ()=>{ try { if (typeof window !== 'undefined' && window.localStorage) { window.localStorage.setItem('forceMetaReconnect','1'); } setConnected(false); setConfig(null); await api.post('/integration/disconnect/'); await refreshConfig(); alert('Conexión reiniciada. Ejecuta nuevamente el flujo para actualizar número.'); } catch(e){ alert('Error desconectando: '+(e.response?.data?.error||e.message)); } }} style={{alignSelf:'flex-start',background:'#fff',border:'1px solid #dc3545',color:'#dc3545',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>Desconectar / Reintentar flujo</button>
        <button type="button" onClick={manualMetaActivation} style={{alignSelf:'flex-start',background:'#fff',border:'1px solid #0ea5e9',color:'#0ea5e9',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>🛠️ Completar activación</button>
        <button type="button" onClick={refreshConfig} style={{alignSelf:'flex-start',background:'#fff',border:'1px solid #28a745',color:'#1d6f34',borderRadius:4,padding:'4px 10px',cursor:'pointer',fontSize:12}}>Refrescar estado</button>
      </div>
    );
  }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:8}}>
      <div style={{fontSize:11,color:'#64748b'}}>build: 2025-12-16</div>
      <div style={{background:'#fff3cd',border:'1px solid #b38b00',color:'#503800',padding:'10px',borderRadius:'6px',fontSize:'13px'}}>
        Debug: Coexistencia bundle activo (app/) — {CONFIG_ID ? 'CONFIG_ID OK' : 'CONFIG_ID faltante'}
      </div>
      <button onClick={launchWhatsAppSignup} disabled={!sdkReady || launching || CONFIG_ID === '<CONFIGURATION_ID>'} style={buttonStyle} type="button">
        {launching ? 'Iniciando...' : 'Login with Facebook (Coexistencia)'}
      </button>
      <button onClick={manualMetaActivation} style={{background:'#fff',border:'1px solid #0ea5e9',color:'#0ea5e9',borderRadius:4,padding:'6px 10px',cursor:'pointer',fontSize:13,alignSelf:'flex-start'}} type="button" title="Completar activación con credenciales guardadas">
        🛠️ Completar activación
      </button>
      <div style={{display:'flex',gap:8,alignItems:'center'}}>
        <label htmlFor="clientPhoneNumber" style={{fontSize:13,color:'#0f172a',fontWeight:'600'}}>Tu número (E.164):</label>
        <input id="clientPhoneNumber" value={clientPhoneNumber} onChange={(e)=>setClientPhoneNumber(e.target.value)} placeholder="Ej: +593958646043" autoComplete="off" autoCorrect="off" autoCapitalize="off" inputMode="numeric" style={{fontSize:13,padding:'8px 10px',border:'1px solid #0f172a',borderRadius:6,width:'260px',color:'#0f172a',background:'#fff'}} />
      </div>
      {config?.signup_state === 'pending' && (<div style={{fontSize:13,color:'#0f172a'}}>Estado actual: pendiente. Completa el flujo para activar.</div>)}
    </div>
  );
};

export default EmbeddedSignupButton;
